/**
 * VPE Form Dialog Component
 * Handles the add/edit VPE form dialog
 * Extracted from ManageVPE.tsx for better modularity
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

interface VPEFormDialogProps {
  // Dialog State
  open: boolean;
  onOpenChange: (open: boolean) => void;
  
  // Form State
  isEditMode: boolean;
  isCreateLoading: boolean;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  taskAssigned: boolean;
  showPassword: boolean;
  showConfirmPassword: boolean;
  
  // Form Actions
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onTaskAssignedChange: (checked: boolean | "indeterminate") => void;
  onShowPasswordToggle: () => void;
  onShowConfirmPasswordToggle: () => void;
  
  // Actions
  onSubmit: () => void;
  onCancel: () => void;
}

const VPEFormDialog: React.FC<VPEFormDialogProps> = ({
  open,
  onOpenChange,
  isEditMode,
  isCreateLoading,
  firstName,
  lastName,
  email,
  password,
  confirmPassword,
  taskAssigned,
  showPassword,
  showConfirmPassword,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onTaskAssignedChange,
  onShowPasswordToggle,
  onShowConfirmPasswordToggle,
  onSubmit,
  onCancel,
}) => {
  // Check if form has any data (with null safety)
  const hasFormData = (firstName || '').trim() || (lastName || '').trim() || (email || '').trim() || (password || '').trim() || (confirmPassword || '').trim() || taskAssigned;

  // Custom handler for dialog open change
  const handleOpenChange = (newOpen: boolean) => {
    // If trying to close the dialog and there's form data, prevent it
    if (!newOpen && hasFormData) {
      // Don't close the dialog - user must use Cancel or Create/Update buttons
      return;
    }
    
    // Otherwise, allow normal behavior
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md bg-white rounded-2xl shadow-2xl border-0" data-testid="vpe-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-semibold text-gray-800" data-testid="vpe-form-dialog-title">
            {isEditMode ? 'Update Virtual Patient Enroller Details' : 'Add Virtual Patient Enroller'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 mt-4" data-testid="vpe-form-content">
          <div className="grid grid-cols-2 gap-4" data-testid="vpe-name-fields">
            <Input
              placeholder="First Name"
              value={firstName}
              onChange={(e) => onFirstNameChange(e.target.value)}
              className="border-purple-200 focus:border-purple-400 rounded-lg"
              autoComplete="off"
              data-testid="vpe-first-name-input"
            />
            <Input
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => onLastNameChange(e.target.value)}
              className="border-purple-200 focus:border-purple-400 rounded-lg"
              autoComplete="off"
              data-testid="vpe-last-name-input"
            />
          </div>
          
          <Input
            placeholder="Email Address"
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            className="border-purple-200 focus:border-purple-400 rounded-lg"
            autoComplete="off"
            data-testid="vpe-email-input"
          />
          
          <div className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg" data-testid="vpe-task-assignment">
            <span className="text-sm text-gray-700 font-medium">Assigned Tasks:</span>
            <Checkbox
              checked={taskAssigned}
              onCheckedChange={onTaskAssignedChange}
              className="border-purple-300 data-[state=checked]:bg-purple-600"
              data-testid="vpe-task-assigned-checkbox"
            />
            <span className="text-sm text-gray-700">Deferred</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4" data-testid="vpe-password-fields">
            <div className="relative">
              <Input
                placeholder={isEditMode ? "Password (optional)" : "Password"}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => onPasswordChange(e.target.value)}
                className="border-purple-200 focus:border-purple-400 rounded-lg pr-10"
                autoComplete="new-password"
                data-testid="vpe-password-input"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-purple-100"
                onClick={onShowPasswordToggle}
                data-testid="vpe-password-toggle"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <div className="relative">
              <Input
                placeholder={isEditMode ? "Confirm Password (optional)" : "Confirm Password"}
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => onConfirmPasswordChange(e.target.value)}
                className="border-purple-200 focus:border-purple-400 rounded-lg pr-10"
                autoComplete="new-password"
                data-testid="vpe-confirm-password-input"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-purple-100"
                onClick={onShowConfirmPasswordToggle}
                data-testid="vpe-confirm-password-toggle"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          
          <div className="flex space-x-4 pt-4" data-testid="vpe-form-buttons">
            <Button
              onClick={onSubmit}
              disabled={isCreateLoading}
              className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg py-3 font-medium shadow-lg hover:shadow-xl transition-all duration-200"
              data-testid="vpe-submit-button"
            >
              {isCreateLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                </div>
              ) : (
                isEditMode ? 'Update' : 'Create'
              )}
            </Button>
            <Button
              onClick={onCancel}
              variant="outline"
              className="flex-1 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white border-pink-500 rounded-lg py-3 font-medium shadow-lg hover:shadow-xl transition-all duration-200"
              data-testid="vpe-cancel-button"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VPEFormDialog;